import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../utils/apiHelper';
import UserContext from '../context/UserContext';
import AddFileToProject from './AddFileToProject';
import AddFileToPlaylist from './AddFileToPlaylist';
import PlaylistDisplay from './PlaylistDisplay';
import FilesDisplay from './FilesDisplay';

//Shows the general files for a project 
//Shows the general files for a project 
//Shows the general files for a project 

const ProjectFiles = () => {
  const { authUser } = useContext(UserContext);
  const { projectId } = useParams();

  const [projectFiles, setProjectFiles] = useState([]);
  const [playList, setPlayList] = useState([]);
  const [projectTitle, setProjectTitle] = useState('Untitled Project');

  const [filesLoaded, setFilesLoaded] = useState(false);
  const [playlistLoaded, setPlaylistLoaded] = useState(false);
  const [error, setError] = useState(null);

  const loading = !(filesLoaded && playlistLoaded);

  // --- fetch general files ---
  const fetchProjectFiles = useCallback(async () => {
    try {
      setFilesLoaded(false);
      const userId = authUser?._id;
      if (!userId) throw new Error('User not authenticated');

      const res = await api(`/get-project-files?userId=${userId}&projectId=${projectId}`, 'GET');
      const files = Array.isArray(res?.files) ? res.files : [];
      setProjectFiles(files);
    } catch (e) {
      setProjectFiles([]);
      setError(e.message || 'Failed to load project files');
      console.error('Error fetching project files:', e);
    } finally {
      setFilesLoaded(true);
    }
  }, [authUser, projectId]);

  // --- fetch playlist files ---
  const fetchPlaylistFiles = useCallback(async () => {
    try {
      setPlaylistLoaded(false);
      const userId = authUser?._id;
      if (!userId) throw new Error('User not authenticated');

      const res = await api(`/get-playlist-files?userId=${userId}&projectId=${projectId}`, 'GET');
      const plist = Array.isArray(res?.playlistFiles) ? res.playlistFiles : [];
      setPlayList(plist);
    } catch (e) {
      setPlayList([]);
      setError(e.message || 'Failed to load playlist files');
      console.error('Error fetching playlist files:', e);
    } finally {
      setPlaylistLoaded(true);
    }
  }, [authUser, projectId]);

  // initial + on id change
  useEffect(() => {
    if (authUser && projectId) {
      fetchProjectFiles();
      fetchPlaylistFiles();
    }
  }, [authUser, projectId, fetchProjectFiles, fetchPlaylistFiles]);

  // derive title from either source
  useEffect(() => {
    const titleFromFiles = projectFiles[0]?.projectTitle;
    const titleFromPlaylist = playList[0]?.projectTitle;
    setProjectTitle(titleFromFiles || titleFromPlaylist || 'Untitled Project');
  }, [projectFiles, playList]);

  // keep your existing remove for GENERAL files only
  const removeFile = async (fileId) => {
    try {
      const userId = authUser?._id;
      if (!userId) throw new Error('User not authenticated');
      await api('/delete-file', 'DELETE', { userId, projectId, fileId });
      fetchProjectFiles(); // refresh general files only
    } catch (e) {
      console.error(`Error deleting file ${fileId}`, e);
    }
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error: {error}</p>;

  return (
    <div className="mt-5">
      <h3>Project: {projectTitle}</h3>

      {/* General Files */}
      <AddFileToProject projectId={projectId} onFileUploadSuccess={fetchProjectFiles} />
      {projectFiles.length > 0 ? (
        <FilesDisplay
          files={projectFiles}
          setFiles={setProjectFiles}
          onPlayUploadSuccess={fetchPlaylistFiles}
          onRemove={removeFile}
          theId={authUser._id}
          projectId={projectId}
        />
      ) : (
        <p>No general files yet.</p>
      )}

      {/* Playlist (always visible, independent of general files) */}
      <h3 className="mt-6">Playlist</h3>
      <AddFileToPlaylist projectId={projectId} onPlayUploadSuccess={fetchPlaylistFiles} />
      {playList.length > 0 ? (
        <PlaylistDisplay
          playlistFiles={playList}
          projectId={projectId}
          onSave={async (reordered) => {
            const userId = authUser?._id;
            if (!userId) return;
            const payload = reordered.map((f, i) => ({ ...f, seqPos: i + 1 }));
            try {
              const resp = await api('/save-reordered-play', 'PUT', { projectId, files: payload, userId });
              if (resp?.success) fetchPlaylistFiles();
            } catch (e) {
              console.error('Error saving reordered playlist files:', e);
            }
          }}
        />
      ) : (
        <p>No playlist files yet.</p>
      )}
    </div>
  );
};

export default ProjectFiles;